/**
 * Annotations Panel Component
 * 
 * Displays claim annotations grouped by severity with navigation to fields.
 */

import React, { memo, useCallback, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { formatAnnotationMessage, getFieldDisplayTitle, getSeverityColor } from './utils/annotationHelpers';
import type { FieldAnnotations, GroupedAnnotations } from './types';

interface AnnotationsPanelProps {
  /** Grouped annotations to display */
  annotations: GroupedAnnotations;
  /** Callback when a field is clicked */
  onFieldClick?: (fieldPath: string, cmsBox?: string | null) => void;
  /** Whether to show the panel */
  open?: boolean;
  /** Callback when panel is toggled */
  onToggle?: () => void;
  /** Whether to show as collapsible or always open */
  collapsible?: boolean;
  /** Whether this panel is in sidebar mode */
  isSidebar?: boolean;
}

interface FieldAnnotationItemProps {
  fieldAnn: FieldAnnotations;
  onFieldClick?: (fieldPath: string, cmsBox?: string | null) => void;
}

/**
 * Memoized component for rendering individual field annotations
 * Extracted outside to prevent recreation on every render
 */
const FieldAnnotationItem = memo<FieldAnnotationItemProps>(({ fieldAnn, onFieldClick }) => {
  const handleClick = useCallback(() => {
    if (fieldAnn.formFieldPath && onFieldClick) {
      onFieldClick(fieldAnn.formFieldPath, fieldAnn.cmsBox);
    }
  }, [fieldAnn.formFieldPath, fieldAnn.cmsBox, onFieldClick]);

  const isClickable = Boolean(fieldAnn.formFieldPath && onFieldClick);

  return (
    <ListItem
      disablePadding
      sx={{
        borderLeft: 3,
        borderColor: `${getSeverityColor(fieldAnn.highestSeverity)}.main`,
        mb: 1,
        backgroundColor: 'background.paper',
      }}
    >
      <ListItemButton
        onClick={handleClick}
        disabled={!isClickable}
        sx={{
          cursor: isClickable ? 'pointer' : 'default',
          '&:hover': isClickable ? {
            backgroundColor: 'action.hover',
          } : {},
        }}
      >
        <ListItemText
          disableTypography
          primary={
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                {getFieldDisplayTitle(fieldAnn)}
              </Typography>
              {fieldAnn.annotations.length > 1 && (
                <Chip
                  label={fieldAnn.annotations.length}
                  size="small"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Stack>
          }
          secondary={
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {fieldAnn.annotations.map((ann, idx) => (
                <Typography
                  key={idx}
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem' }}
                >
                  • {formatAnnotationMessage(ann)}
                </Typography>
              ))}
            </Stack>
          }
        />
      </ListItemButton>
    </ListItem>
  );
});

FieldAnnotationItem.displayName = 'FieldAnnotationItem';

const AnnotationsPanelBase: React.FC<AnnotationsPanelProps> = ({
  annotations,
  onFieldClick,
  open = true,
  onToggle,
  collapsible = true,
  isSidebar = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['errors', 'warnings', 'infos']) // Expand all sections by default
  );

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const renderSection = (
    title: string,
    fields: FieldAnnotations[],
    severity: 'error' | 'warning' | 'info',
    count: number
  ) => {
    if (fields.length === 0) return null;

    const sectionKey = severity + 's'; // errors, warnings, info
    const isExpanded = expandedSections.has(sectionKey);

    return (
      <Accordion
        expanded={isExpanded}
        onChange={() => toggleSection(sectionKey)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          backgroundColor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 0,
            minHeight: 48,
            '& .MuiAccordionSummary-content': {
              margin: '8px 0',
            },
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography
              variant="subtitle1"
              color={`${severity}.main`} sx={{ fontWeight: 500 }}
            >
              {title}
            </Typography>
            <Chip
              label={count}
              size="small"
              color={severity}
              sx={{ height: 22 }}
            />
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <List dense sx={{ py: 0 }}>
            {fields.map(fieldAnn => (
              <FieldAnnotationItem
                key={fieldAnn.fieldKey}
                fieldAnn={fieldAnn}
                onFieldClick={onFieldClick}
              />
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
    );
  };

  const content = (
    <Box sx={{ 
      p: isSidebar ? 1.5 : 2,
      pb: isSidebar ? 8 : 2, // Extra bottom padding in sidebar mode
      className: 'sidebar-content' 
    }}>
      {/* Summary Alert */}
      {annotations.totalCount === 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontSize: isSidebar ? '0.875rem' : '1rem' }}>
            No Issues Found
          </AlertTitle>
          <Typography variant="body2" sx={{ fontSize: isSidebar ? '0.75rem' : '0.875rem' }}>
            The claim validation completed successfully with no issues.
          </Typography>
        </Alert>
      ) : (
        <>
          {annotations.errorCount > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontSize: isSidebar ? '0.75rem' : '0.875rem' }}>
                <strong>{annotations.errorCount} error{annotations.errorCount > 1 ? 's' : ''}</strong> must be fixed before submission
              </Typography>
            </Alert>
          )}
          {annotations.warningCount > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontSize: isSidebar ? '0.75rem' : '0.875rem' }}>
                <strong>{annotations.warningCount} warning{annotations.warningCount > 1 ? 's' : ''}</strong> should be reviewed
              </Typography>
            </Alert>
          )}
        </>
      )}

      {/* Instructions - moved above annotations */}
      {annotations.totalCount > 0 && onFieldClick && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ 
            display: 'block', 
            mt: 2,
            mb: 1,
            fontStyle: 'italic',
            fontSize: isSidebar ? '0.7rem' : '0.75rem' 
          }}
        >
          Click on any item to navigate to the field
        </Typography>
      )}

      {/* Grouped Annotations */}
      {annotations.totalCount > 0 && (
        <Stack spacing={0} sx={{ mt: 1 }}>
          {renderSection('Errors', annotations.errors, 'error', annotations.errorCount)}
          {renderSection('Warnings', annotations.warnings, 'warning', annotations.warningCount)}
          {renderSection('Information', annotations.info, 'info', annotations.infoCount)}
        </Stack>
      )}
    </Box>
  );

  // In sidebar mode, don't show the collapsible header - the sidebar itself handles that
  if (isSidebar) {
    // No Paper wrapper in sidebar mode - the ResizableSidebar provides the container
    return content;
  }
  
  if (!collapsible) {
    return (
      <Paper variant="outlined">
        {content}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined">
      {/* Collapsible Header - only shown when not in sidebar mode */}
      <Box
        sx={{
          p: 2,
          borderBottom: open ? 1 : 0,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Validation Results
          </Typography>
          {!open && annotations.totalCount > 0 && (
            <Stack direction="row" spacing={1}>
              {annotations.errorCount > 0 && (
                <Chip
                  label={`${annotations.errorCount} error${annotations.errorCount > 1 ? 's' : ''}`}
                  size="small"
                  color="error"
                />
              )}
              {annotations.warningCount > 0 && (
                <Chip
                  label={`${annotations.warningCount} warning${annotations.warningCount > 1 ? 's' : ''}`}
                  size="small"
                  color="warning"
                />
              )}
              {annotations.errorCount === 0 && annotations.warningCount === 0 && annotations.infoCount > 0 && (
                <Chip
                  label={`${annotations.infoCount} info`}
                  size="small"
                  color="info"
                />
              )}
            </Stack>
          )}
        </Stack>
        <IconButton size="small">
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={open}>{content}</Collapse>
    </Paper>
  );
};

// Memoize component to prevent unnecessary re-renders
export const AnnotationsPanel = memo(AnnotationsPanelBase);
AnnotationsPanel.displayName = 'AnnotationsPanel';